import React, { forwardRef } from "react";
import { useTheme } from "@repo/tokens";
import { resolveThemeToken } from "../../utils/theme";
import {
  type TitleProps,
  type TitleVariant,
  type TitleRole,
  type TitleSize,
  type TitleWeight,
} from "./types";

type TokenSizeKey = "small" | "medium" | "large";
type TokenWeightKey = "regular" | "emphasized" | "bold";

const VARIANT_TAGS: Record<TitleVariant, string> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
};

const DEFAULT_ROLE_BY_VARIANT: Record<TitleVariant, TitleRole> = {
  h1: "display",
  h2: "headline",
  h3: "title",
  h4: "title",
  h5: "title",
  h6: "title",
};

const DEFAULT_SIZE_BY_VARIANT: Record<TitleVariant, TitleSize> = {
  h1: "xl",
  h2: "lg",
  h3: "md",
  h4: "sm",
  h5: "sm",
  h6: "xs",
};

const SIZE_TOKEN_MAP: Record<TitleSize, TokenSizeKey> = {
  xs: "small",
  sm: "small",
  md: "medium",
  lg: "large",
  xl: "large",
};

function mapColorToken(color?: string): string | undefined {
  if (!color) {
    return undefined;
  }
  if (color === "primary") {
    return "text.primary";
  }
  if (color === "secondary") {
    return "text.secondary";
  }
  if (color === "tertiary") {
    return "text.tertiary";
  }
  if (color === "disabled") {
    return "text.disabled";
  }
  if (color === "error") {
    return "feedback.error";
  }
  if (color === "success") {
    return "feedback.success";
  }
  if (color === "warning") {
    return "feedback.warning";
  }
  return color;
}

function getTokenWeightKey(weight?: TitleWeight): TokenWeightKey {
  if (!weight) {
    return "bold";
  }
  if (typeof weight === "number") {
    return "bold";
  }
  if (weight === "regular") {
    return "regular";
  }
  if (weight === "medium" || weight === "emphasized") {
    return "emphasized";
  }
  return "bold";
}

function getNumericWeight(weight?: TitleWeight): string | undefined {
  if (typeof weight === "number") {
    return String(weight);
  }
  if (!weight) {
    return undefined;
  }
  if (weight === "regular") {
    return "400";
  }
  if (weight === "medium" || weight === "emphasized") {
    return "500";
  }
  if (weight === "semibold") {
    return "600";
  }
  if (weight === "bold") {
    return "700";
  }
  return undefined;
}

function resolveMargin(
  theme: any,
  value: number | string | undefined,
): string | number | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (typeof value === "number") {
    return value;
  }
  const resolved = resolveThemeToken(theme, value);
  return resolved ?? value;
}

export const Title = forwardRef<HTMLElement, TitleProps>((props, ref) => {
  const {
    variant = "h1",
    role,
    size,
    weight = "bold",
    color,
    align = "left",
    margin,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    letterSpacing,
    lineHeight,
    fontSize,
    fontFamily,
    truncate,
    htmlTag,
    webstyle,
    className,
    id,
    style,
    title,
    children,
    ...rest
  } = props;

  const { theme } = useTheme();

  const resolvedVariant: TitleVariant = variant;
  const resolvedRole: TitleRole =
    role ?? DEFAULT_ROLE_BY_VARIANT[resolvedVariant];
  const resolvedSize: TitleSize =
    size ?? DEFAULT_SIZE_BY_VARIANT[resolvedVariant];
  const sizeKey: TokenSizeKey = SIZE_TOKEN_MAP[resolvedSize];
  const weightKey: TokenWeightKey = getTokenWeightKey(weight);

  const baseTypography = (theme.typography as any)?.[resolvedRole]?.[sizeKey]?.[
    weightKey
  ];

  const numericWeight = getNumericWeight(weight);

  const mappedColorToken = mapColorToken(color);
  const resolvedColorToken = mappedColorToken ?? "text.primary";
  const finalColor =
    resolveThemeToken(theme, resolvedColorToken) ?? resolvedColorToken;

  const componentTag = VARIANT_TAGS[resolvedVariant] || "h1";
  const Component = (htmlTag || componentTag) as any;
  const level = parseInt(resolvedVariant.slice(1), 10) || 1;
  const isNativeHeadingTag =
    Component === "h1" ||
    Component === "h2" ||
    Component === "h3" ||
    Component === "h4" ||
    Component === "h5" ||
    Component === "h6";

  const styles: React.CSSProperties = {
    color: finalColor,
    fontFamily: fontFamily ?? baseTypography?.fontFamily ?? "Poppins",
    fontSize:
      fontSize !== undefined ? `${fontSize}px` : baseTypography?.fontSize,
    lineHeight: lineHeight ?? baseTypography?.lineHeight,
    fontWeight: numericWeight ?? baseTypography?.fontWeight,
    letterSpacing:
      letterSpacing !== undefined
        ? `${letterSpacing}px`
        : baseTypography?.letterSpacing,
    textAlign: align as any,
    margin: resolveMargin(theme, margin),
    marginTop: resolveMargin(theme, marginTop),
    marginBottom: resolveMargin(theme, marginBottom),
    marginLeft: resolveMargin(theme, marginLeft),
    marginRight: resolveMargin(theme, marginRight),
    ...((style || {}) as React.CSSProperties),
  };

  if (truncate) {
    styles.whiteSpace = "nowrap";
    styles.overflow = "hidden";
    styles.textOverflow = "ellipsis";
  }

  const ariaProps: Record<string, unknown> = {};
  if (!isNativeHeadingTag) {
    ariaProps.role = "heading";
    ariaProps["aria-level"] = level;
  }

  return (
    <Component
      ref={ref}
      className={className || webstyle}
      id={id}
      style={!(className || webstyle) ? styles : undefined}
      {...ariaProps}
      {...rest}
    >
      {children || title}
    </Component>
  );
});

Title.displayName = "Title";
